import { useNavigate, type LinkProps, type NavigateOptions } from '@tanstack/react-router'

/**
 * The savings area's own paths.
 *
 * They are built as plain strings and widened once, here, rather than at forty call sites. The
 * router's `to` prop is typed against the registered route tree, and this area's routes are
 * registered by the integrator after it is written — see `savingsRoutes` in
 * `src/routes/savings/index.ts`. Every path in this file appears in that array, so the two cannot
 * drift without the array being wrong, and once the routes are wired the cast is a formality
 * rather than a hole.
 */

const path = (value: string) => value as LinkProps['to']

export const savingsPaths = {
  accounts: () => path('/savings'),
  account: (accountId: string) => path(`/savings/accounts/${encodeURIComponent(accountId)}`),
  accountCash: (accountId: string) =>
    path(`/savings/accounts/${encodeURIComponent(accountId)}/cash`),
  products: () => path('/savings/products'),
  runs: () => path('/savings/runs'),
  customer: (customerId: string) => path(`/savings/customers/${encodeURIComponent(customerId)}`),
}

export interface SavingsNavigateOptions {
  search?: Record<string, unknown>
  replace?: boolean
}

/**
 * Navigation within savings, with the same widening applied to the search object.
 *
 * The router types `search` against whatever route `to` names, and these routes are not in the
 * tree yet; the schemas that will validate them are exported from `src/routes/savings/search.ts`
 * for the integrator to attach.
 */
export function useSavingsNavigate() {
  const navigate = useNavigate()

  return (to: LinkProps['to'], options: SavingsNavigateOptions = {}) => {
    // Widened through `unknown` for the same reason `to` is: the router types a route's search
    // object against the route itself, and these routes are not in the tree yet. The schemas in
    // `src/routes/savings/search.ts` are what actually validate these values, on the way in.
    const request: unknown = { to, ...options }
    void navigate(request as NavigateOptions)
  }
}
